import type { PartnerResponse } from '@palaccount/contracts';

/** One currency's figure on one side of the account. */
export interface BalanceLine {
  currency: string;
  value: string;
}

/** The fields a balance display reads; `balances` optional for older fixtures. */
export type BalanceSource = Pick<PartnerResponse, 'owedToUs' | 'currency'> & {
  owedByUs?: string | undefined;
  balances?: PartnerResponse['balances'] | undefined;
};

/**
 * The partner's balance on one side, ONE LINE PER CURRENCY (owner decision,
 * 30 Oct 2026: "owes $500 and ₪2,000" — never a mixed sum). Read off the
 * server's `balances` rows, default currency first as the server orders them;
 * a currency whose figure on THIS side is zero is left out. With nothing
 * open anywhere the partner's own currency shows its (zero) scalar figure, so
 * the display never goes blank.
 */
export function balanceLines(partner: BalanceSource, side: 'customer' | 'supplier'): BalanceLine[] {
  const lines = (partner.balances ?? [])
    .map((row) => ({
      currency: row.currency,
      value: side === 'customer' ? row.owedToUs : row.owedByUs,
    }))
    .filter((line) => !/^-?0+(\.0+)?$/.test(line.value));
  if (lines.length > 0) return lines;
  return [
    {
      currency: partner.currency,
      value: side === 'customer' ? partner.owedToUs : (partner.owedByUs ?? '0.00'),
    },
  ];
}
